import type {ReactElement} from 'react';
import React, {useRef, useState} from 'react';
import {InputCounter} from '@Components/input-counter';
import type {ControlledSliderProperties} from '@Components/controlled-slider/controlled-slider.types';
import {ControlledSlider} from '@Components/controlled-slider';
import {Text, TextSize} from '@Components/text';
import {type InputFieldControls, InputFieldSize, InputFieldState} from '@Components/input-field-v2/input-field.types';
import {SliderOptionsContainer} from '@Components/slider-options/components/slider-options-container/slider-options-container';
import styles from './slider-options.module.scss';

export interface SliderOptionsRange {
  min: number;
  max: number;
}

export interface SliderOptionsProps extends Omit<ControlledSliderProperties, 'min' | 'max'> {
  id?: string;
  className?: string;
  inputCounterClassName?: string;
  value: number;
  label: string;
  /**
   * this is for the transformed value
   */
  minimumStepFromInputField?: number;
  /**
   * this has the highest preference and is for the original value
   * THIS IS ONLY needed if both ranges are the same, and you still want to round off the original values to a specific decimal place
   */
  precision?: number;
  /**
   * normally anything you send gets displayed as a value between 0 -100
   * these values are transformed, if you want a different scale then you can send your own transformDisplayTo
   * Similarly the step by default is for the transformed range not the original range
   */
  range?: SliderOptionsRange;
  transformDisplayTo?: SliderOptionsRange;

  onChange(val: number): void;

  onChangeEnded?(val: number): void;

  wrapText?: boolean;
  state?: InputFieldState;
  size?: InputFieldSize;
  isValueBlank?: boolean;
  typingStoppedTimeout?: number;
  controlIcons?: string[];
  hideSlider?: boolean;
  inputFieldControls?: InputFieldControls;
  inputFieldClassname?: string;

  overrideDefaultContols?: boolean;
  controlsDisabledStates?: boolean[];
  onClickControl?(controlIndex: number): void;
}

const TYPING_STOPPED_TIMEOUT = 600;

export function SliderOptions({
  className = '',
  inputCounterClassName = '',
  id = '',
  useMarksAsSteps = false,
  marks = [],
  step = 1,
  wrapText = false,
  minimumStepFromInputField = step,
  isDisabled = false,
  size = InputFieldSize.SMALL,
  range = {
    min: 0,
    max: 100,
  },
  transformDisplayTo = {
    min: 0,
    max: 100,
  },
  state = InputFieldState.DEFAULT,
  isValueBlank = false,
  typingStoppedTimeout = TYPING_STOPPED_TIMEOUT,
  hideSlider = false,
  overrideDefaultContols = false,
  onClickControl = (): void => {},
  ...props
}: SliderOptionsProps): ReactElement {
  const sliderTimer = useRef<number | undefined>();
  const [outOfBoundsValue, setOutOfBoundsValue] = useState<number>(0);
  const outOfBoundsTimer = useRef<number | undefined>();
  const [isOutOfBounds, setIsOutOfBounds] = useState<boolean>(false);

  const findNumberOfDecimalPlaces = (valueToUse = minimumStepFromInputField): number => {
    try {
      const mantissa = String(valueToUse).split('.')[1];
      if (!mantissa) {
        return 0;
      }
      return mantissa.length;
    } catch {
      return 0;
    }
  };

  const isValueGreaterThanMax = (val: number): boolean => {
    return val > range.max;
  };

  const isValueSmallerThanMin = (val: number): boolean => {
    return val < range.min;
  };

  const processUpdatedValue = (val: number): number => {
    const roundTo = getNumberOfDecimalPlacesForOriginalValue();
    return parseFloat(val.toFixed(roundTo));
  };

  const getNumberOfDecimalPlacesForOriginalValue = (): number => {
    const numDecimalPlaces = 1;
    if (props.precision) {
      const decimalPlacesFromPrecision = findNumberOfDecimalPlaces(props.precision);
      if (decimalPlacesFromPrecision === 0) {
        return numDecimalPlaces;
      }
      return decimalPlacesFromPrecision;
    }

    const decimalPlacesFromRanges = findNumberOfDecimalPlaces(getStepFromRanges());
    if (decimalPlacesFromRanges === 0) {
      return numDecimalPlaces;
    }
    return decimalPlacesFromRanges;
  };

  const getPrecisionForOriginalValue = (): number => {
    if (props.precision) {
      return 1 / 10 ** findNumberOfDecimalPlaces(props.precision);
    }
    return 1 / 10 ** findNumberOfDecimalPlaces(getStepFromRanges());
  };

  const onChangeHandler = (value: number): void => {
    props.onChange(value);
    if (sliderTimer) {
      clearTimeout(sliderTimer.current);
    }
    sliderTimer.current = window.setTimeout(() => {
      const roundTo = getPrecisionForOriginalValue();
      let newVal = Math.round(value / roundTo) * roundTo;

      if (isValueGreaterThanMax(newVal)) {
        props.onChange(processUpdatedValue(range.max));
        if (props.onChangeEnded) {
          props.onChangeEnded(processUpdatedValue(range.max));
        }
      } else if (isValueSmallerThanMin(newVal)) {
        props.onChange(processUpdatedValue(range.min));
        if (props.onChangeEnded) {
          props.onChangeEnded(processUpdatedValue(range.min));
        }
      } else {
        newVal = processUpdatedValue(newVal);
        props.onChange(newVal);
        if (props.onChangeEnded) {
          props.onChangeEnded(newVal);
        }
      }
    }, typingStoppedTimeout);
  };
  const onSlideHandler = (value: number): void => {
    if (!(value > range.max || value < range.min)) {
      props.onChange(value);
    }
  };

  const transformDisplay = (from: SliderOptionsRange, to: SliderOptionsRange, value: number): number => {
    return ((value - from.min) / (from.max - from.min)) * (to.max - to.min) + to.min;
  };

  const transformValueToDisplay = (val: number): number => {
    if (!transformDisplayTo) {
      return val;
    }
    const calculatedValue: string = transformDisplay(range, transformDisplayTo, val).toFixed(findNumberOfDecimalPlaces());
    return parseFloat(calculatedValue);
  };

  const transformValueFromDisplay = (val: number): number => {
    if (!transformDisplayTo) {
      return val;
    }
    const calculatedValue: string = transformDisplay(transformDisplayTo, range, val).toFixed(getNumberOfDecimalPlacesForOriginalValue());
    return parseFloat(calculatedValue);
  };

  const getMin = (): number => {
    if (transformDisplayTo) {
      return transformDisplayTo.min;
    }
    return range.min;
  };

  const getMax = (): number => {
    if (transformDisplayTo) {
      return transformDisplayTo.max;
    }
    return range.max;
  };

  const getValueForDisplay = (): number => {
    if (isOutOfBounds) {
      return outOfBoundsValue;
    }
    if (transformDisplayTo) {
      return parseFloat(transformValueToDisplay(props.value).toFixed(findNumberOfDecimalPlaces()));
    }

    return parseFloat(parseFloat(String(props.value)).toFixed(findNumberOfDecimalPlaces()));
  };

  const convertValueFromDisplay = (value: number): number => {
    if (transformDisplayTo) {
      return transformValueFromDisplay(value);
    }
    return value;
  };

  const isValueOutOfBounds = (val: number): boolean => {
    return val < range.min || val > range.max;
  };

  const handleOutOfBoundsValue = (val: number): void => {
    setOutOfBoundsValue(val);
    if (outOfBoundsTimer) {
      clearTimeout(outOfBoundsTimer.current);
    }

    outOfBoundsTimer.current = window.setTimeout((): void => {
      setIsOutOfBounds(false);
      setOutOfBoundsValue(props.value);
    }, typingStoppedTimeout);
  };

  const preOnChangeHandler = (value: number): void => {
    const convertedValue = convertValueFromDisplay(value);

    if (isValueOutOfBounds(convertedValue)) {
      if (!isOutOfBounds) {
        if (convertedValue > range.max) {
          onChangeHandler(range.max);
        } else {
          onChangeHandler(range.min);
        }
        setIsOutOfBounds(true);
      }
      handleOutOfBoundsValue(value);
      return;
    }

    if (isOutOfBounds) {
      setIsOutOfBounds(false);
    }

    onChangeHandler(convertedValue);
  };

  const getStepFromRanges = (): number => {
    const transformedRange = transformDisplayTo.max - transformDisplayTo.min;
    const originalRange = range.max - range.min;

    const stepsBetweenTransformedRange = transformedRange / step;

    return originalRange / stepsBetweenTransformedRange;
  };

  const preOnSlideHandler = (value: number): void => {
    const convertedValue = convertValueFromDisplay(value);
    onSlideHandler(convertedValue);
  };

  const getSlider = (): ReactElement => {
    if (hideSlider) {
      return <></>;
    }

    return (
      <ControlledSlider
        onSlideEnded={(val) => {
          const convertedValue = convertValueFromDisplay(val);
          if (props.onChangeEnded) {
            props.onChangeEnded(convertedValue);
          }
        }}
        onSlide={preOnSlideHandler}
        value={getValueForDisplay()}
        min={getMin()}
        max={getMax()}
        useMarksAsSteps={useMarksAsSteps}
        marks={marks}
        step={step}
        isDisabled={isDisabled}
      />
    );
  };

  return (
    <SliderOptionsContainer
      id={id}
      className={className}
      upperContainer={
        <>
          <Text
            val={props.label}
            size={TextSize.XSMALL}
            className={`${styles.text} ${wrapText ? styles.wrapText : styles.noWrap} ${isDisabled ? 'color-extra-light' : 'content-sub-text'} spacing-m-r-5`}
          />
          <InputCounter
            controlIcons={props.controlIcons}
            className={`${styles.inputCounter} ${inputCounterClassName}`}
            value={getValueForDisplay()}
            onInputChange={preOnChangeHandler}
            controlsDisabledStates={props.controlsDisabledStates ?? [getValueForDisplay() <= getMin(), getValueForDisplay() >= getMax()]}
            min={getMin()}
            max={getMax()}
            step={step}
            minimumStepFromInputField={minimumStepFromInputField}
            isDisabled={isDisabled}
            elementTitle={props.label}
            state={state}
            size={size}
            isValueBlank={isValueBlank}
            inputFieldControls={props.inputFieldControls}
            inputFieldClassname={props.inputFieldClassname}
            overrideDefaultContols={overrideDefaultContols}
            onClickControl={(index): void => {
              onClickControl(index);
            }}
          />
        </>
      }
      slider={getSlider()}
    />
  );
}
